"use client";

import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { TierItem } from "@/components/sortable-tier-item";

export function useTierManagement(initialTiers: TierItem[]) {
  const [localTiers, setLocalTiers] = useState<TierItem[]>(initialTiers);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingTierIndex, setEditingTierIndex] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const addTier = () => {
    const newTier: TierItem = {
      id: uuidv4(),
      label: "",
      labelColor: "#000000",
      backgroundColor: "#CCCCCC",
    };
    setLocalTiers([...localTiers, newTier]);
  };

  const removeTier = (index: number) => {
    const newTiers = localTiers.filter((_, i) => i !== index);
    setLocalTiers(newTiers);
  };

  const openColorModal = (index: number) => {
    setEditingTierIndex(index);
    setColorModalOpen(true);
  };

  const closeColorModal = () => {
    setColorModalOpen(false);
  };

  const handleLabelChange = (index: number, label: string) => {
    const newTiers = [...localTiers];
    newTiers[index] = {
      ...newTiers[index],
      label,
    };
    setLocalTiers(newTiers);
  };

  const saveColors = (labelColor: string, backgroundColor: string) => {
    if (editingTierIndex !== null) {
      const newTiers = [...localTiers];
      newTiers[editingTierIndex] = {
        ...newTiers[editingTierIndex],
        labelColor,
        backgroundColor,
      };
      setLocalTiers(newTiers);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = localTiers.findIndex((tier) => tier.id === active.id);
      const newIndex = localTiers.findIndex((tier) => tier.id === over.id);

      setLocalTiers((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  return {
    localTiers,
    setLocalTiers,
    colorModalOpen,
    editingTierIndex,
    activeId,
    addTier,
    removeTier,
    openColorModal,
    closeColorModal,
    handleLabelChange,
    saveColors,
    handleDragStart,
    handleDragEnd,
  };
}
